import { Navigation } from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { BarChart } from "@tremor/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Session } from "@supabase/supabase-js";

interface DashboardProps {
  session: Session;
}

const mockVendors = [
  { name: "Amazon", items: 1200 },
  { name: "eBay", items: 800 },
  { name: "Walmart", items: 600 },
  { name: "Target", items: 400 },
];

const mockApiRequests = 15234;
const mockItemsIngested = 3000;

const Dashboard = ({ session }: DashboardProps) => {
  const [selectedVendor, setSelectedVendor] = useState<string>("all");
  const [date, setDate] = useState<Date>();

  const filteredData = selectedVendor === "all" 
    ? mockVendors 
    : mockVendors.filter(v => v.name === selectedVendor);

  const chartData = filteredData.map(vendor => ({
    name: vendor.name,
    "Items": vendor.items,
  }));

  return (
    <div className="min-h-screen bg-skuhub-navy">
      <Navigation session={session} />
      <div className="container mx-auto px-4 py-24">
        <h1 className="text-4xl font-bold text-white mb-8">Dashboard</h1>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <h3 className="text-lg font-semibold text-white">API Requests</h3>
            <p className="text-3xl font-bold text-skuhub-coral mt-2">
              {mockApiRequests.toLocaleString()}
            </p>
          </Card>
          
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <h3 className="text-lg font-semibold text-white">Active Vendors</h3>
            <p className="text-3xl font-bold text-skuhub-coral mt-2">
              {mockVendors.length}
            </p>
          </Card>
          
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <h3 className="text-lg font-semibold text-white">Items Ingested</h3>
            <p className="text-3xl font-bold text-skuhub-coral mt-2">
              {mockItemsIngested.toLocaleString()}
            </p>
          </Card>
        </div>

        <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
          <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-white">Items by Vendor</h3>
            <div className="flex gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal bg-white/5",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger className="w-[180px] bg-white/5">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {mockVendors.map((vendor) => (
                    <SelectItem key={vendor.name} value={vendor.name}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="h-[400px] mt-4">
            <BarChart
              data={chartData}
              index="name"
              categories={["Items"]}
              colors={["rose"]}
              valueFormatter={(number) => number.toLocaleString()}
              className="h-full w-full text-white [&_text]:fill-white [&_line]:stroke-white/20"
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
